/* ==========================================================================
   OPV — Product form
   <opv-product-form> orquesta: variantes, cantidad escalonada, suscripción
   vs compra única, precio en vivo y add-to-cart AJAX.
   ========================================================================== */
(function () {
  'use strict';

  var money = function (cents) {
    return window.OPV.money(cents, window.OPV.moneyFormat);
  };

  /* ---------------------------------------------------------------------
     Galería con miniaturas
     --------------------------------------------------------------------- */
  class OpvGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-opv-gallery-viewport]');
      this.thumbs = Array.from(this.querySelectorAll('[data-opv-thumb]'));
      this.slides = Array.from(this.querySelectorAll('[data-opv-gallery-slide]'));
      this.thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () { this.select(i); }.bind(this));
      }, this);
      if (this.viewport) {
        this.viewport.addEventListener('scroll', function () {
          clearTimeout(this._t);
          this._t = setTimeout(this.syncThumbs.bind(this), 90);
        }.bind(this), { passive: true });
      }
      // Flechas prev/next
      var step = function (dir) {
        var next = this.currentIndex() + dir;
        var max = this.slides.length - 1;
        if (next > max) next = 0;
        if (next < 0) next = max;
        this.select(next);
      }.bind(this);
      this.querySelectorAll('[data-opv-gal-prev]').forEach(function (b) {
        b.addEventListener('click', function () { step(-1); });
      });
      this.querySelectorAll('[data-opv-gal-next]').forEach(function (b) {
        b.addEventListener('click', function () { step(1); });
      });

      document.addEventListener('opv:variant:change', this.onVariant.bind(this));
    }
    currentIndex() {
      if (!this.viewport) return 0;
      var pos = this.viewport.scrollLeft;
      var best = 0, dist = Infinity;
      this.slides.forEach(function (s, i) {
        var d = Math.abs(s.offsetLeft - this.viewport.offsetLeft - pos);
        if (d < dist) { dist = d; best = i; }
      }, this);
      return best;
    }
    select(i) {
      var slide = this.slides[i];
      if (!slide || !this.viewport) return;
      this.viewport.scrollTo({ left: slide.offsetLeft - this.viewport.offsetLeft, behavior: 'smooth' });
      this.setActiveThumb(i);
    }
    setActiveThumb(i) {
      this.thumbs.forEach(function (t, ti) {
        t.classList.toggle('is-active', ti === i);
        t.setAttribute('aria-current', ti === i ? 'true' : 'false');
      });
    }
    syncThumbs() {
      var pos = this.viewport.scrollLeft;
      var best = 0, dist = Infinity;
      this.slides.forEach(function (s, i) {
        var d = Math.abs(s.offsetLeft - this.viewport.offsetLeft - pos);
        if (d < dist) { dist = d; best = i; }
      }, this);
      this.setActiveThumb(best);
    }
    onVariant(e) {
      var mediaId = e.detail.variant && e.detail.variant.featured_media
        ? String(e.detail.variant.featured_media.id) : null;
      if (!mediaId) return;
      var index = this.slides.findIndex(function (s) { return s.dataset.mediaId === mediaId; });
      if (index > -1) this.select(index);
    }
  }
  if (!customElements.get('opv-gallery')) customElements.define('opv-gallery', OpvGallery);

  /* ---------------------------------------------------------------------
     Formulario de producto
     --------------------------------------------------------------------- */
  class OpvProductForm extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-opv-product-json]');
      if (!data) return;
      this.product = JSON.parse(data.textContent);
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-opv-variant-id]');
      this.qtyInput = this.querySelector('[data-opv-quantity-input]');
      this.planInput = this.querySelector('[data-opv-selling-plan-input]');
      this.button = this.querySelector('[data-opv-atc]');
      this.showPriceInButton = this.dataset.priceInButton !== 'false';

      this.optionInputs = Array.from(this.querySelectorAll('[data-opv-option-index]'));
      this.tierInputs = Array.from(this.querySelectorAll('[data-opv-tier]'));
      this.planInputs = Array.from(this.querySelectorAll('[data-opv-purchase-type]'));

      var onChange = this.update.bind(this);
      this.optionInputs.forEach(function (i) { i.addEventListener('change', onChange); });
      this.tierInputs.forEach(function (i) { i.addEventListener('change', onChange); });
      this.planInputs.forEach(function (i) { i.addEventListener('change', onChange); });

      if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.update();
    }

    get selectedOptions() {
      var values = [];
      this.optionInputs.forEach(function (input) {
        if (input.type === 'radio' && !input.checked) return;
        values[parseInt(input.dataset.opvOptionIndex, 10)] = input.value;
      });
      return values;
    }

    get variant() {
      var wanted = this.selectedOptions;
      if (!wanted.length) return this.product.variants[0];
      return this.product.variants.find(function (v) {
        return wanted.every(function (val, i) { return val === undefined || v.options[i] === val; });
      }) || null;
    }

    get tier() {
      var checked = this.tierInputs.find(function (i) { return i.checked; });
      if (!checked) return { quantity: 1, discount: 0, variantId: null };
      return {
        quantity: parseInt(checked.dataset.opvTier, 10) || 1,
        discount: parseFloat(checked.dataset.discount || '0'),
        variantId: checked.dataset.variantId ? parseInt(checked.dataset.variantId, 10) : null
      };
    }

    get sellingPlanId() {
      var checked = this.planInputs.find(function (i) { return i.checked; });
      if (!checked || checked.value === 'onetime') return null;
      var variant = this.variant;
      var groupId = checked.dataset.sellingPlanGroup;
      var planId = checked.dataset.sellingPlan;
      if (!variant || !variant.selling_plan_allocations) return planId ? parseInt(planId, 10) : null;
      var alloc = variant.selling_plan_allocations.find(function (a) {
        if (planId) return String(a.selling_plan_id) === String(planId);
        if (groupId) return String(a.selling_plan_group_id) === String(groupId);
        return true;
      });
      return alloc ? alloc.selling_plan_id : null;
    }

    unitPrice(variant, planId) {
      if (planId && variant.selling_plan_allocations) {
        var alloc = variant.selling_plan_allocations.find(function (a) {
          return String(a.selling_plan_id) === String(planId);
        });
        if (alloc) return alloc.price;
      }
      return variant.price;
    }

    update() {
      var variant = this.variant;
      var tier = this.tier;
      var planId = this.sellingPlanId;

      // Variante efectiva: la tarjeta de cantidad puede apuntar a otra variante
      if (tier.variantId) {
        var override = this.product.variants.find(function (v) { return v.id === tier.variantId; });
        if (override) variant = override;
      }

      this.querySelectorAll('[data-opv-option-index]').forEach(function (input) {
        if (input.type !== 'radio') return;
        var label = input.closest('label');
        if (label) label.classList.toggle('is-selected', input.checked);
      });
      this.tierInputs.forEach(function (input) {
        var card = input.closest('[data-opv-tier-card]');
        if (card) card.classList.toggle('is-selected', input.checked);
      });
      this.planInputs.forEach(function (input) {
        var card = input.closest('[data-opv-plan-card]');
        if (card) card.classList.toggle('is-selected', input.checked);
        var extra = card && card.querySelector('[data-opv-plan-extra]');
        if (extra) extra.hidden = !input.checked;
      });

      if (!variant) {
        this.setUnavailable(window.OPV.strings ? window.OPV.strings.unavailable : 'No disponible');
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      if (this.qtyInput) this.qtyInput.value = tier.quantity;
      if (this.planInput) this.planInput.value = planId || '';

      var unit = this.unitPrice(variant, planId);
      var total = unit * tier.quantity;
      var compareUnit = variant.compare_at_price && variant.compare_at_price > unit
        ? variant.compare_at_price : null;
      // Si hay % de ahorro declarado en la tarjeta y no hay compare_at, lo derivamos
      if (!compareUnit && tier.discount > 0) {
        compareUnit = Math.round(unit / (1 - tier.discount / 100));
      }
      var compareTotal = compareUnit ? compareUnit * tier.quantity : null;

      this.setText('[data-opv-price]', money(total));
      this.setText('[data-opv-price-unit]', money(unit));
      this.querySelectorAll('[data-opv-compare-price]').forEach(function (el) {
        el.hidden = !compareTotal;
        if (compareTotal) el.textContent = money(compareTotal);
      });
      this.querySelectorAll('[data-opv-savings]').forEach(function (el) {
        el.hidden = !compareTotal;
        if (compareTotal) el.textContent = money(compareTotal - total);
      });

      // Precio por tarjeta de cantidad.
      // Cada tarjeta puede apuntar a su propia variante (data-variant-id). Su
      // precio tiene que salir de ESA variante, no de la seleccionada: con el
      // modelo de "un paquete = una variante" las tres filas mostraban todas
      // el importe de la fila marcada.
      this.tierInputs.forEach(function (input) {
        var card = input.closest('[data-opv-tier-card]');
        if (!card) return;
        var qty = parseInt(input.dataset.opvTier, 10) || 1;
        var propia = null;
        if (input.dataset.variantId) {
          propia = this.product.variants.find(function (v) {
            return String(v.id) === String(input.dataset.variantId);
          });
        }
        var base = propia || variant;
        var unitCard = this.unitPrice(base, planId);
        var priceEl = card.querySelector('[data-opv-tier-price]');
        if (priceEl) priceEl.textContent = money(unitCard * qty);
        var eachEl = card.querySelector('[data-opv-tier-each]');
        if (eachEl) eachEl.textContent = money(unitCard);
      }, this);

      // Precio por opción de compra (suscripción / única)
      this.planInputs.forEach(function (input) {
        var card = input.closest('[data-opv-plan-card]');
        if (!card) return;
        var pid = input.value === 'onetime' ? null : this.resolvePlan(variant, input);
        var planUnit = this.unitPrice(variant, pid);
        var priceEl = card.querySelector('[data-opv-plan-price]');
        if (priceEl) priceEl.textContent = money(planUnit * tier.quantity);

        // Tachado por tarjeta: compare_at real, o el precio de compra única
        // cuando la suscripción sale más barata.
        var cmpEl = card.querySelector('[data-opv-plan-compare]');
        if (cmpEl) {
          var ref = variant.compare_at_price && variant.compare_at_price > planUnit
            ? variant.compare_at_price
            : (variant.price > planUnit ? variant.price : null);
          cmpEl.hidden = !ref;
          if (ref) cmpEl.textContent = money(ref * tier.quantity);
        }
      }, this);

      if (variant.available) {
        this.button.disabled = false;
        var label = this.button.querySelector('[data-opv-atc-label]');
        if (label) label.textContent = this.button.dataset.labelAdd || 'Agregar al carrito';
        var btnPrice = this.button.querySelector('[data-opv-atc-price]');
        if (btnPrice) {
          btnPrice.hidden = !this.showPriceInButton;
          btnPrice.textContent = money(total);
        }
        var btnCompare = this.button.querySelector('[data-opv-atc-compare]');
        if (btnCompare) {
          btnCompare.hidden = !this.showPriceInButton || !compareTotal;
          if (compareTotal) btnCompare.textContent = money(compareTotal);
        }
      } else {
        this.setUnavailable(this.button.dataset.labelSoldOut || 'Agotado');
      }

      document.dispatchEvent(new CustomEvent('opv:variant:change', {
        detail: { variant: variant, quantity: tier.quantity, sellingPlan: planId }
      }));

      if (window.history.replaceState && this.dataset.updateUrl !== 'false') {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    resolvePlan(variant, input) {
      var planId = input.dataset.sellingPlan;
      var groupId = input.dataset.sellingPlanGroup;
      if (!variant.selling_plan_allocations) return planId ? parseInt(planId, 10) : null;
      var alloc = variant.selling_plan_allocations.find(function (a) {
        if (planId) return String(a.selling_plan_id) === String(planId);
        if (groupId) return String(a.selling_plan_group_id) === String(groupId);
        return true;
      });
      return alloc ? alloc.selling_plan_id : null;
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach(function (el) { el.textContent = value; });
    }

    setUnavailable(label) {
      if (!this.button) return;
      this.button.disabled = true;
      var l = this.button.querySelector('[data-opv-atc-label]');
      if (l) l.textContent = label;
      var p = this.button.querySelector('[data-opv-atc-price]');
      if (p) p.hidden = true;
    }

    onSubmit(e) {
      e.preventDefault();
      var variant = this.variant;
      var tier = this.tier;
      if (tier.variantId) {
        var override = this.product.variants.find(function (v) { return v.id === tier.variantId; });
        if (override) variant = override;
      }
      if (!variant || !variant.available) return;

      var item = { id: variant.id, quantity: tier.quantity };
      var planId = this.sellingPlanId;
      if (planId) item.selling_plan = planId;

      var extras = Array.from(document.querySelectorAll('[data-opv-bundle-addon]:checked'))
        .map(function (input) { return { id: parseInt(input.value, 10), quantity: 1 }; });

      var error = this.querySelector('[data-opv-form-error]');
      if (error) error.hidden = true;

      window.OPV.addToCart({ items: [item].concat(extras) }, this.button)
        .catch(function (err) {
          if (error) { error.hidden = false; error.textContent = err.message; }
          else alert(err.message);
        });
    }
  }
  if (!customElements.get('opv-product-form')) customElements.define('opv-product-form', OpvProductForm);
})();
